import React, { useState, useEffect } from 'react';

// material-ui
import { Button, Box, FormControl, Grid, InputLabel, Link, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography} from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

//components
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import PdfObject from '../dataObjects/PdfObject';

interface Props {
    processForm: (form: PdfObject) => void;
}

const PdfScanning = (props: Props): JSX.Element => {

    //set up state for the app
    const [pdfs, setPdfs] = useState<PdfObject[]>();
    const [selectedForm, setSelectedForm] = useState<PdfObject | null>(null);
    const [pdfLink, setPdfLink] = useState<string>('');
    const [pdfName, setPdfName] = useState<string>('');
    const [timeStampSize, setTimeStampSize] = useState(24);
    const [timeStampTime, setTimeStampTime] = useState(new Date().toLocaleString('en-US', { hour12: true }))
    const [rotationDegree, setRotationDegree] = useState(90);
    const [pageSize, setPageSize] = useState('letter');
    const [pageFit, setPageFit] = useState('fit');
    const [isProcessing, setIsProcessing] = useState(false);
    const [iframeKey, setIframeKey] = useState(0);

    //payload
    useEffect(() => {
        getPdfsToProcess();
    }, []);


    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    const getPdfsToProcess = async () => {
        await fetch(`/LLMFormProcessor/GetUnprocessedPdfs`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetUnprocessedPdfs',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let pdfs = result.result;
                    setPdfs(pdfs);
                    setSelectedForm(pdfs[0]);
                    setPdfName(pdfs[0].Name);
                    setPdfLink(pdfs[0].Uri);
                }
            }
            )
    }

    const setSelectedPdf = (pdf: PdfObject) => {
        setSelectedForm(pdf);
        setPdfLink(pdf.Uri);
        setPdfName(pdf.Name);
        setIframeKey(0);
    }

    const uploadNewPdf = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);

        fetch(`/Paperless/AddNewPdf`,
            {
                method: 'POST',
                body: formData,
            })
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: /Paperless/AddNewPdf',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    getPdfsToProcess();
                }
            });
    }

    const deletePdf = (uri: string) => {
        let url = `/Paperless/DeletePdfToProcess?uri=${uri}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: `/Paperless/DeletePdfToProcess',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    getPdfsToProcess();
                }
            })
    }

    /*TimeStamp Related*/

    const updateTimeStampSize = (event: any) => {
        setTimeStampSize(event.target.value);
    }

    const addTimeStamp = () => {

        let stampText = "Office of Campaign and Political Finance";
        if (pdfName) {
            let url = `/Paperless/AddTimeStampPdf?url=${pdfLink}&stampText=${stampText}&stampDate=${timeStampTime}&stampColor=BLUE&stampSize=${timeStampSize}`
            fetch(url)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: AddWatermark',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        getPdfsToProcess();
                    }
                })
        }
        else {
            let info = {
                type: 'error',
                title: 'Error: No Form Selected',
                errorMessage: 'Please select the form from the PDF list',
                secondaryErrorMessage: ''
            };
            setAlertInfo(info);
            setAlertInfoDialog(true);
        }
    }

    const removeTimeStamp = () => {
        if (pdfLink) {
            deletePdf(pdfLink);
        }
    }


    /*PDF tools related*/

    const updateRotationDegree = (event: any) => {
        setRotationDegree(event.target.value);
    } 

    const rotatePdf = () => {

        if (pdfName) {
            let url = `/Paperless/RotateIntakePdf?url=${pdfLink}&degrees=${rotationDegree}&pages=`
            fetch(url)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: Rotate Pdf',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        setIframeKey(prev => prev + 1);
                        getPdfsToProcess();
                    }
                })
        }
        else {
            let info = {
                type: 'error',
                title: 'Error: No Form Selected',
                errorMessage: 'Please select the form from the PDF list',
                secondaryErrorMessage: ''
            };
            setAlertInfo(info);
            setAlertInfoDialog(true);
        }
    }

    const updatePdfSize = (event: any) => {
        let newSize = event.target.value;
        setPageSize(newSize);
    }

    const updatePdfFit = (event: any) => {
        let newFit = event.target.value;
        setPageFit(newFit);
    }

    const resizeAndFitPdf = () => {
        if (pdfName) {
            let url = `/Paperless/ResizeIntakePdf?url=${pdfLink}&pageSize=${pageSize}&mode=${pageFit}`
            fetch(url)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: Resize Pdf',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        setIframeKey(prev => prev + 1);
                        getPdfsToProcess();
                    }
                })
        }
        else {
            let info = {
                type: 'error',
                title: 'Error: No Form Selected',
                errorMessage: 'Please select the form from the PDF list',
                secondaryErrorMessage: ''
            };
            setAlertInfo(info);
            setAlertInfoDialog(true);
        }
    }


    const revertPdf = () => {
        if (pdfName) {
            let url = `/Paperless/RevertIntakePdf?url=${pdfLink}`
            fetch(url)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: Revert Pdf',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        setIframeKey(prev => prev + 1);
                        getPdfsToProcess();
                    }
                })
        }
        else {
            let info = {
                type: 'error',
                title: 'Error: No Form Selected',
                errorMessage: 'Please select the form from the PDF list',
                secondaryErrorMessage: ''
            };
            setAlertInfo(info);
            setAlertInfoDialog(true);
        }
    }

    const processForm = () => {
        if (selectedForm && !isProcessing) {
            setIsProcessing(true);
            props.processForm(selectedForm);
        }
    }

    return (
        <>
            <Grid item xs={3.5} component={Paper} marginRight={3} >
                <Stack spacing={1} >
                    { /*upload*/}
                    <Typography variant='body1' fontWeight={550}>Upload a new form to process:</Typography>
                    <form onSubmit={(event: React.FormEvent<HTMLFormElement>) => uploadNewPdf(event)}>
                        <Stack direction="row" spacing={1} alignItems={"center"} >
                            <input type="file" name="newPdf" />
                            <Button variant="outlined" color="success" type="submit">Upload PDF</Button>
                        </Stack>
                    </form>
                    { /*pdf list*/}
                    <Box sx={{ height: 600, p: 1, overflow: 'auto' }}>
                        <Typography variant='body1' fontWeight={550} marginTop={3}>Select a scanned pdf to process:</Typography>
                        <TableContainer>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><Typography variant='body2' fontWeight={550}>Name</Typography></TableCell>
                                        <TableCell><Typography variant='body2' fontWeight={550}>Created</Typography></TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pdfs && pdfs.map((pdf: PdfObject) =>
                                        <TableRow key={pdf.Name} onClick={() => setSelectedPdf(pdf)} selected={selectedForm?.Name === pdf.Name} sx={{ '&.Mui-selected': { backgroundColor: '#e3f2fd' }, cursor: 'pointer' }}>
                                            <TableCell>
                                                <Typography variant="body2" component={Link} underline="none">{pdf.Name}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{pdf.LastModifiedDateDisplay}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <DeleteOutlinedIcon className="is-clickable" color="error" onClick={() => deletePdf(pdf.Uri)} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Stack>
            </Grid>
            <Grid item xs={6.5} component={Paper} >
                <Stack spacing={1.5} marginBottom={2} >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2" color="blue">Time Stamp:</Typography>
                        <FormControl sx={{ m: 1, minWidth: 80 }} size="small">
                            <InputLabel>Size</InputLabel>
                            <Select
                                label="Size"
                                value={timeStampSize}
                                onChange={(event) => updateTimeStampSize(event)}
                            >
                                <MenuItem value={24}>60%</MenuItem>
                                <MenuItem value={40}>100%</MenuItem>
                                <MenuItem value={36}>90%</MenuItem>
                                <MenuItem value={32}>80%</MenuItem>
                                <MenuItem value={28}>70%</MenuItem>
                                <MenuItem value={20}>50%</MenuItem>
                                <MenuItem value={16}>40%</MenuItem>
                                <MenuItem value={12}>30%</MenuItem>
                                <MenuItem value={8}>20%</MenuItem>
                                <MenuItem value={4}>10%</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Edit Time"
                            size="small"
                            value={timeStampTime}
                            onChange={(event) => { setTimeStampTime(event.target.value) }}
                        />
                        <Button variant="outlined" size="small" onClick={addTimeStamp} >Add Time Stamp</Button>
                        <Button variant="outlined" color="error" size="small" onClick={removeTimeStamp}>Remove Time Stamp</Button>
                        <Button variant="outlined" color="success" size="small" startIcon={<PictureAsPdfIcon />} onClick={processForm} disabled={isProcessing} >
                            {isProcessing ? 'Processing...' : 'Process Form'}
                        </Button>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2" color="blue">PDF Tools:</Typography>
                        <FormControl sx={{ m: 1, minWidth: 80 }} size="small">
                            <InputLabel>Rotate</InputLabel>
                            <Select
                                label="Rotate"
                                value={rotationDegree}
                                onChange={(event) => updateRotationDegree(event)}
                            >
                                <MenuItem value={90}>90° CW</MenuItem>
                                <MenuItem value={180}>180°</MenuItem>
                                <MenuItem value={270}>270°</MenuItem>
                            </Select>
                        </FormControl>
                        <Button variant="outlined" size="small" onClick={rotatePdf} >Rotate</Button>
                        <FormControl sx={{ m: 1, minWidth: 80 }} size="small">
                            <InputLabel>Resize</InputLabel>
                            <Select
                                label="Resize"
                                value={pageSize}
                                onChange={(event) => updatePdfSize(event)}
                            >
                                <MenuItem value="letter">Letter (8.5 x 11)</MenuItem>
                                <MenuItem value="letter-landscape">Letter landscape</MenuItem>
                                <MenuItem value="legal">Legal (8.5 x 14)</MenuItem>
                                <MenuItem value="legal-landscape">Legal landscape</MenuItem>
                                <MenuItem value="tabloid">Tabloid (11 x 17)</MenuItem>
                                <MenuItem value="a4">A4</MenuItem>
                                <MenuItem value="a4-landscape">A4 landscape</MenuItem>
                                <MenuItem value="a3">A3</MenuItem>
                                <MenuItem value="custom">Custom (pts)</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl sx={{ m: 1, minWidth: 80 }} size="small">
                            <InputLabel>Fit?</InputLabel>
                            <Select
                                label="Fit?"
                                value={pageFit}
                                onChange={(event) => updatePdfFit(event)}
                            >
                                <MenuItem value={'fit'}>Fit</MenuItem>
                                <MenuItem value={'stretch'}>Stretch</MenuItem>
                            </Select>
                        </FormControl>
                        <Button variant="outlined" size="small" onClick={resizeAndFitPdf} >Resize</Button>
                        <Button variant="outlined" color="error" size="small" onClick={revertPdf}>Revert Rotate/Resize</Button>
                    </Stack>
                    <div>
                        {
                            pdfLink &&
                            <iframe key={iframeKey} src={pdfLink} width="100%" height="600px" />
                        }
                    </div>
                </Stack>
            </Grid>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
    );
};

export default PdfScanning;